import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../../api/predictions.ts';
import { claves } from '../../api/queries.ts';

/**
 * La respuesta ENTERA, no solo `matches`: trae además la racha viva del usuario y los
 * umbrales que la premian. Recortarla aquí con un `select` obligaría a una segunda
 * consulta para pintar la racha, que es el mismo dato pedido dos veces.
 */
export function useLeagueMatches(leagueId: string) {
  return useQuery({
    queryKey: claves.ligaPronosticos(leagueId),
    queryFn: () => api.listMyPredictions(leagueId),
  });
}

/**
 * Envío del pronóstico.
 *
 * Al terminar —bien o mal— se invalida la lista. Importa sobre todo cuando FALLA con un
 * 409: significa que el servidor considera el partido cerrado y el `is_open` que tiene
 * la pantalla está caduco, así que hay que volver a preguntar en vez de dejar la
 * interfaz afirmando que aún se puede editar.
 */
export function useSubmitPrediction(leagueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { matchId: number; home: number; away: number }) =>
      api.submitPrediction(input.matchId, input.home, input.away),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: claves.ligaPronosticos(leagueId) });
    },
  });
}

export function useMatchPredictions(leagueId: string, matchId: number, habilitado: boolean) {
  return useQuery({
    queryKey: claves.pronosticosDelPartido(leagueId, matchId),
    queryFn: () => api.listMatchPredictions(leagueId, matchId),
    // Solo se pide cuando el SERVIDOR ya ha dicho que el partido empezó. Pedirlo antes
    // devolvería un 409 y enseñaría un error donde solo hay que esperar.
    enabled: habilitado,
    select: (data) => data.predictions,
  });
}

/**
 * Vuelve a preguntar al servidor cuando pasa el kickoff más próximo de los abiertos.
 *
 * El reloj del navegador NO decide si el plazo se cierra —eso lo dice `is_open`, que
 * viene de PostgreSQL—, pero sí sirve para saber CUÁNDO conviene volver a preguntar.
 * Sin esto, una pestaña abierta desde antes del partido seguiría enseñando el formulario
 * como editable hasta que el usuario recargara, y solo se enteraría al recibir un 409.
 */
export function useRefrescoAlKickoff(
  matches: api.LeagueMatch[] | undefined,
  refrescar: () => void,
): void {
  const proximo = matches
    ?.filter((m) => m.is_open)
    .map((m) => Date.parse(m.kickoff_time))
    .filter((t) => Number.isFinite(t))
    .sort((a, b) => a - b)[0];

  useEffect(() => {
    if (proximo === undefined) return;
    // Un segundo de margen: el reloj del cliente puede ir ligeramente adelantado y no
    // queremos preguntar justo antes de que el servidor cambie de opinión.
    const espera = proximo - Date.now() + 1000;
    if (espera <= 0) {
      refrescar();
      return;
    }
    // Más de un día por delante no merece un temporizador: el refresco al recuperar el
    // foco de la ventana ya cubre ese caso.
    if (espera > 24 * 60 * 60 * 1000) return;
    const id = setTimeout(refrescar, espera);
    return () => clearTimeout(id);
  }, [proximo, refrescar]);
}
